import { IPromise } from 'angular';
import * as _ from 'underscore';

import { CheckBoxWithLabelProps } from '@tableau/widgets';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { DialogAlertProps } from '../../common/react/components/dialogAlerts/DialogAlerts';
import { FeatureFlags } from '../FeatureFlags';
import { ISiteRoleFilter } from '../filters/SiteRoleFilter';
import { IActionContext } from '../helpers/ActionContext';
import { ResultHelper } from '../helpers/ResultHelper';
import { ISiteRolePickerMenuScope, SiteRolePickerMenuHelper } from '../helpers/SiteRolePickerMenuHelper';
import { L10n } from '../L10n/L10n';
import { IGroupDetailsInfo } from '../models/IGroupDetailsInfo';
import { IGroupBasicInfo, IGroupInfo } from '../models/IGroupInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Groups } from '../resources/Groups';
import { SiteRoleOption, SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { SiteRolesResource } from '../resources/SiteRoles';
import { GrantLicenseModes } from '../ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ServerService } from './ServerService';
import { ToasterService, ToastType } from './ToasterService';

export interface ISetGroupsMinimumSiteRoleDialogScope extends ISiteRolePickerMenuScope {
  description: string;
  siteRole: string;
  siteRoleOptions: string[];
  setSiteRole: (role: string) => void;
  licenseUponLoginCheckboxProps: CheckBoxWithLabelProps;
  grantLicenseMode: GrantLicenseModes;
  dialogInfoAlertProps: DialogAlertProps;
  isUnlicensed: boolean;
}

export class UpdateGroupsMinimumSiteRoleAction {

  public static $inject = [
    '$translate',
    '$q',
    '$filter',
    'Groups',
    'ServerService',
    'SiteRoles',
    'SiteRoleOptionsResource',
    'ToasterService',
    'ConfirmActionDialog'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private $q: ng.IQService,
    private $filter: ng.IFilterService,
    private Groups: Groups,
    private server: ServerService,
    private siteRoles: SiteRolesResource,
    private siteRoleOptionsResource: SiteRoleOptionsResource,
    private toaster: ToasterService,
    private confirmActionDialog: ConfirmActionDialog
  ) { }

  public executeInPlace(group: IGroupDetailsInfo, siteRole: string, grantLicenseMode: GrantLicenseModes) {
    const groupSet = Set.fromIdItemArray([group]);

    return this.Groups.updateGroupsMinimumSiteRole([group.id], siteRole, grantLicenseMode)
      .then(ResultHelper.rejectResultOnError(this.$q))
      .then(() => this.handleSuccessResult(groupSet, siteRole))
      .catch((result: IResult) => this.handleErrorResult(groupSet, siteRole, result));
  }

  public showUpdateGroupsMinimumSiteRoleDialog(
    groupsToSet: Set<IGroupInfo>,
    context: IActionContext): IPromise<any> {

    const groupsToUpdate = FeatureFlags.licenseUponLoginEnabled ? groupsToSet : groupsToSet.filter(Groups.isActiveDirectory);

    if(groupsToUpdate.size() === 0) {
      // No active directory groups were selected, display an error message
      return this.confirmActionDialog.open({
        scope: context.scope,
        title: this.$translate.instant('minimumSiteRole'),
        bodyText: this.$translate.instant('minimumSiteRoleNoADGroups')
      }).result;
    }

    const dialogScope = <ISetGroupsMinimumSiteRoleDialogScope> context.scope.$new();

    dialogScope.siteRole = (groupsToUpdate.size() === 1) ? groupsToUpdate.first().minimumSiteRole : SiteRoles.Unlicensed;

    dialogScope.siteRoleOptions = this.siteRoles.optionsForCurrentUserExcept(SiteRoles.ServerAdministrator);
    dialogScope.setSiteRole = (role: string) => {
      dialogScope.siteRole = role;
    };

    const onSiteRoleSelected = (roleOption: SiteRoleOption) => {
      dialogScope.$apply(() => {
        dialogScope.siteRoleSelectorProps.selectedRole = roleOption;
        dialogScope.setSiteRole(roleOption.role);
        dialogScope.isUnlicensed = roleOption.role === SiteRoles.Unlicensed;
        dialogScope.licenseUponLoginCheckboxProps =
        {...dialogScope.licenseUponLoginCheckboxProps,
            disabled: dialogScope.isUnlicensed,
            checked: dialogScope.grantLicenseMode === GrantLicenseModes.ON_LOGIN};
        });
    };

    const GroupsSiteRoleDropdownWidth = 260;

    SiteRolePickerMenuHelper.setupPickerMenuScope(dialogScope, this.$translate, this.server, this.siteRoleOptionsResource, dialogScope.siteRole, onSiteRoleSelected, true);
    dialogScope.siteRoleSelectorProps.style = { width: GroupsSiteRoleDropdownWidth - CommonStyles.Sizes.DefaultSpace - (2 * CommonStyles.Sizes.TextBoxBorderSize) };

    dialogScope.description = this.$translate.instant('confirmSetGroupsMinimumSiteRoleDesc', {
      GROUP_NAME: L10n.elideIfLong(groupsToUpdate.first().name),
      GROUP_COUNT: groupsToUpdate.size()
    }, L10n.Interpolations.Count);

    const toggleLicenseUponLogin = (checked: boolean) => {
      dialogScope.grantLicenseMode = checked ? GrantLicenseModes.ON_LOGIN : GrantLicenseModes.ON_SYNC;
      dialogScope.licenseUponLoginCheckboxProps = {...dialogScope.licenseUponLoginCheckboxProps, checked: checked };
      dialogScope.$digest();
    };

    dialogScope.isUnlicensed = dialogScope.siteRole === SiteRoles.Unlicensed;
    dialogScope.grantLicenseMode = (groupsToUpdate.size() === 1) ? groupsToUpdate.first().grantLicenseMode as GrantLicenseModes: GrantLicenseModes.ON_SYNC;

    dialogScope.licenseUponLoginCheckboxProps = {
      checked: dialogScope.grantLicenseMode === GrantLicenseModes.ON_LOGIN,
      handleChange: toggleLicenseUponLogin.bind(this),
      containerStyle: {marginLeft: '0px', marginTop: '8px'},
      label: Messages.grantLicenseUponLoginCheckboxLabel(),
      testId: 'lul-update-ad-group',
      disabled: dialogScope.isUnlicensed
    };

    dialogScope.dialogInfoAlertProps = {
      message: Messages.confirmSetGroupsMinimumSiteRoleWarning(),
      testId: 'group-min-site-role-alert'
    };

    const dialogInstance = this.confirmActionDialog.open({
      title: this.$translate.instant('confirmSetGroupsMinimumSiteRoleTitle'),
      bodyTemplateUrl: 'confirmActionDialogInputRole.html',
      scope: dialogScope,
      confirmDisabled: () => false, // 'confirm' is always enabled for this action.
      actionName: this.$translate.instant('confirmSetGroupsMinimumSiteRoleButton', {
        ITEM_COUNT: groupsToUpdate.size()
      }, L10n.Interpolations.Count),
      action: (dialogInstance: IConfirmActionDialogInstance) => this.setGroupsSiteRoles(groupsToUpdate, dialogScope.siteRole, dialogScope.grantLicenseMode)
    });

    dialogInstance.result.finally(() => dialogScope.$destroy());

    return dialogInstance.result;
  }

  private setGroupsSiteRoles(selectedGroups: Set<IGroupInfo>, siteRole: string, grantLicenseMode: GrantLicenseModes): IPromise<any> {
    const groups = _.map(selectedGroups.toArray(), (group: IGroupInfo) => group.id);

    return this.Groups.updateGroupsMinimumSiteRole(groups, siteRole, grantLicenseMode)
      .then(ResultHelper.rejectResultOnError(this.$q))
      .then(() => this.handleSuccessResult(selectedGroups, siteRole))
      .catch((result: IResult) => this.handleErrorResult(selectedGroups, siteRole, result));
  }

  private handleSuccessResult(groups: Set<IGroupBasicInfo>, siteRole: string) {
    this.toaster.pop({
      type: ToastType.Info,
      text: this.$translate.instant('updateGroupsMinimumSiteRoleSuccess', {
        GROUP_NAME: L10n.elideIfLong(groups.first().name),
        COUNT: groups.size(),
        SITE_ROLE: this.$filter<ISiteRoleFilter>('siteRole')(siteRole)
      }, L10n.Interpolations.Count)
    });
  }

  private handleErrorResult(groups: Set<IGroupBasicInfo>, siteRole: string, result: IResult) {
    this.toaster.pop({
      type: ToastType.Error,
      text: this.$translate.instant('updateGroupsMinimumSiteRoleError', {
        GROUP_NAME: L10n.elideIfLong(groups.first().name),
        COUNT: groups.size(),
        SITE_ROLE: this.$filter<ISiteRoleFilter>('siteRole')(siteRole)
      }, L10n.Interpolations.Count)
    });
    return this.$q.reject(result); // for chaining errors
  }

}

Module.instance().service('UpdateGroupsMinimumSiteRoleAction', UpdateGroupsMinimumSiteRoleAction);
